import React, { useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { FiSmile, FiFrown, FiMeh } from 'react-icons/fi';
import { ChartControls, LoadingSpinner } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor, mergeChartOptions } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useRefineCustomerResponsesByType } from '../../hooks/useRefineCustomerQueries';

interface SurveyResponsesByTypeProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}
const SmileIcon = FiSmile as React.ElementType;
const FrownIcon = FiFrown as React.ElementType;

const SurveyResponsesByType: React.FC<SurveyResponsesByTypeProps> = ({
  branchId = 0,
  showLoading = true,
  onError
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { theme: currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'survey-responses-by-type';
  const componentTitle = 'پاسخ‌های نظرسنجی بر اساس نوع';
  
  // Fetch data using individual hook
  const { data: responsesByType, isLoading, error } = useRefineCustomerResponsesByType(branchId);

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری…"
            />
    );
  }

  if (error || !responsesByType) {
    return null;
  }

  // Chart colors based on theme
  const chartColors = getChartColors(currentTheme);

  // Prepare chart data
  const surveyResponsesData = {
    labels: ['مثبت', 'منفی'],
    datasets: [{
      data: [
        responsesByType['مثبت'] || 0,
        responsesByType['منفی'] || 0
      ],
      backgroundColor: [
        getChartColors(currentTheme)[0],
        getChartColors(currentTheme)[1]
      ],
      borderColor: chartColors,
      borderWidth: 1
    }]
  };

  // Pie chart options using theme helpers
  const pieChartOptions: ChartOptions<'pie'> = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false
      },
      y: {
        display: false
      }
    },
    plugins: {
      legend: {
        // Any pie-specific legend overrides can go here
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const separator = '\u00A0:\u00A0';
            const formattedValue = `${value}%`;
            return `${label}${separator}${formattedValue}`;
          }
        }
      }
    }
  });

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="refineCustomer"
        onInfo={() => openGuide(CHART_GUIDES['survey-responses-type'])}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'refineCustomer',
          componentType: 'chart',
          description: 'نمودار دایره‌ای پاسخ‌های نظرسنجی بر اساس نوع',
          props: { branchId, showLoading, onError },
          gridLayout: {
            containerClass: 'card-base card-padding'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card mb-4">{componentTitle}</h3>
      <div style={{ height: 300, width: '100%' }}>
        <Pie data={surveyResponsesData} options={pieChartOptions} />
      </div>
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <SmileIcon className="text-green-500" />
            <span>پاسخ‌های مثبت</span>
          </span>
          <span className="font-semibold">{formatNumber(responsesByType['مثبت'] || 0)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FrownIcon className="text-red-500" />
            <span>پاسخ‌های منفی</span>
          </span>
          <span className="font-semibold">{formatNumber(responsesByType['منفی'] || 0)}</span>
        </div>
      </div>
    </div>
  );
};

export default SurveyResponsesByType;
